import { API_CONSTANTS } from "../common/api_constants";
import ServerCall from "../serverCall";

// New backend returns money as MoneyDto { amount, currency }. Downstream UI
// reads `.value` (legacy shape), so we remap at the service boundary.
const remapMoney = (m) =>
  m && typeof m === "object" && "amount" in m
    ? { value: m.amount, currencyCode: m.currency }
    : m;

const remapGameHistoryItem = (g) => ({
  ...g,
  entryFee: remapMoney(g.entryFee),
  amountWon: remapMoney(g.amountWon),
  refund: remapMoney(g.refund),
});

const getFeaturedGames = async () => {
  try {
    const response = await ServerCall.get(API_CONSTANTS.appHomepage);
    return response.data;
  } catch (error) {
    console.error("Error fetching featured games:", error);
    throw error;
  }
};

const gameMeta = async () => {
  try {
    const response = await ServerCall.get(API_CONSTANTS.meta);
    return response.data;
  } catch (error) {
    console.error("Error fetching game rules:", error);
    throw error;
  }
};

const allMyResultResult = async ({ page = 1, limit = 100, gameName, lobbyFormat } = {}) => {
  try {
    const params = { page, limit };
    if (gameName && gameName !== "all-games") params.gameName = gameName;
    if (lobbyFormat && lobbyFormat !== "all-formats") params.lobbyFormat = lobbyFormat;

    const response = await ServerCall.get(API_CONSTANTS.result, {
      params,
    });

    const payload = response.data || {};
    const games = Array.isArray(payload.games)
      ? payload.games.map(remapGameHistoryItem)
      : [];

    // Re-wrap in `{ data: { games, ... } }` so existing consumers keep working.
    return {
      data: {
        games,
        pagination: payload.pagination,
        userStats: payload.userStats,
      },
    };
  } catch (error) {
    console.error("Error fetching game history:", error);
    throw error;
  }
};

const revealMatchResult = async (gameRegistrationId) => {
  try {
    const response = await ServerCall.post(API_CONSTANTS.revealResult, {
      gameRegistrationId,
    });
    return response.data;
  } catch (error) {
    console.error("Error revealing match result:", error);
    throw error;
  }
};

const getMatchResultByRegistrationId = async (registrationId) => {
  try {
    const response = await ServerCall.get(
      API_CONSTANTS.rewardMatchByRegistration,
      {
        params: { registrationId },
      },
    );

    const match = response.data?.data?.match;
    if (match) {
      match.amountWon = remapMoney(match.amountWon);
    }

    return response.data;
  } catch (error) {
    console.error("Error fetching match result:", error);
    throw error;
  }
};

const getGameById = async ({ gameName, page = 1, limit = 10 }) => {
  try {
    const response = await ServerCall.get(API_CONSTANTS.gameLobby, {
      params: { gameName, page, limit },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching game lobby:", error);
    throw error;
  }
};

// Collapsed flow: a single POST /game returns { registrationId, gameAuthToken }.
// `lobbyId` was previously passed to the second-backend /game/register; it now
// goes into the same call. `mockState` substitutes for the removed live match-
// making stream.
const getGamePlayDetails = async ({ gameName, lobbyId } = {}) => {
  try {
    const response = await ServerCall.post(API_CONSTANTS.game, {
      gameName,
      ...(lobbyId ? { lobbyId } : {}),
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching game play details:", error);
    throw error;
  }
};

const GameService = {
  getFeaturedGames,
  gameMeta,
  allMyResultResult,
  getGameById,
  getGamePlayDetails,
  getMatchResultByRegistrationId,
  revealMatchResult,
};

export default GameService;
